import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Sparkles, X, ClipboardPaste, Wand2, Loader2, AlertTriangle } from "lucide-react";

/**
 * Shared AI assist modal with two tabs: Paste (text → structured) and Generate (prompt → structured).
 * Uses background-job polling so long-running 7-day plans don't hit edge timeouts.
 * Props:
 *  - open, onClose
 *  - mode: "workout" | "mealplan"
 *  - onApply(parsed): parent decides how to merge the AI result into local state.
 */
export default function AiAssistModal({ open, onClose, mode, onApply }) {
    const [tab, setTab] = useState("paste");
    const [text, setText] = useState("");
    const [prompt, setPrompt] = useState("");
    const [busy, setBusy] = useState(false);
    const [phase, setPhase] = useState(""); // "" | "queued" | "running" | "matching"
    const [elapsed, setElapsed] = useState(0);
    const [err, setErr] = useState("");
    const [result, setResult] = useState(null);
    const pollRef = useRef(null);
    const tickRef = useRef(null);

    useEffect(() => () => stopPolling(), []);

    const stopPolling = () => {
        if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
        if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    };

    if (!open) return null;

    const endpoints = mode === "workout"
        ? { parseType: "parse-workout", generateType: "generate-workout",
            title: "AI Workout Assist", pasteHint: "Paste any workout — I'll turn it into a structured day.",
            genHint: "Describe the day — e.g. \u201CLeg day for glute hypertrophy, 5 exercises, dumbbells only\u201D" }
        : { parseType: "parse-mealplan", generateType: "generate-mealplan",
            title: "AI Meal Plan Assist", pasteHint: "Paste any meal plan (any structure, any day count) — I'll parse it and compute macros.",
            genHint: "Describe the plan — any structure works, e.g. \u201C7-day fat-loss, 2 meals + fruit per day, spinach+eggs every breakfast\u201D" };

    const run = async () => {
        setErr(""); setBusy(true); setResult(null); setElapsed(0); setPhase("queued");
        const startTs = Date.now();
        tickRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startTs) / 1000)), 1000);
        const jobType = tab === "paste" ? endpoints.parseType : endpoints.generateType;
        const payload = tab === "paste" ? { text } : { prompt };
        try {
            const { data: job } = await api.post("/ai/jobs", { type: jobType, payload });
            poll(job.job_id);
        } catch (e) {
            stopPolling();
            setBusy(false); setPhase("");
            setErr(formatApiError(e.response?.data?.detail) || e.message || "Couldn't start AI job");
        }
    };

    const poll = (jobId) => {
        const tick = async () => {
            try {
                const { data: job } = await api.get(`/ai/jobs/${jobId}`);
                if (job.status === "running") setPhase("running");
                if (job.status === "queued") setPhase("queued");
                if (job.status === "done") {
                    stopPolling(); setBusy(false); setPhase("");
                    setResult(job.result);
                    return;
                }
                if (job.status === "error") {
                    stopPolling(); setBusy(false); setPhase("");
                    setErr(job.error || "AI job failed");
                    return;
                }
                pollRef.current = setTimeout(tick, 2000);
            } catch (e) {
                stopPolling(); setBusy(false); setPhase("");
                setErr(formatApiError(e.response?.data?.detail) || e.message || "Lost connection to job");
            }
        };
        tick();
    };

    const apply = () => {
        if (!result) return;
        onApply(result);
        reset();
    };

    const reset = () => {
        stopPolling();
        setText(""); setPrompt(""); setResult(null); setErr(""); setBusy(false); setPhase(""); setElapsed(0); setTab("paste");
        onClose();
    };

    const phaseLabel = phase === "queued" ? "Queued…"
        : phase === "running" ? "Designing your plan…"
        : "Working…";

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={reset} data-testid="ai-assist-modal">
            <div className="bg-[var(--surface)] border border-[var(--gold-deep)] w-full max-w-2xl max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
                    <div className="flex items-center gap-3">
                        <Sparkles size={18} className="text-[var(--gold-bright)]" />
                        <div>
                            <p className="label-tiny mb-1 text-[var(--gold-bright)]">/ AI Assist · Claude Sonnet 4.5</p>
                            <h3 className="font-serif text-2xl">{endpoints.title}</h3>
                        </div>
                    </div>
                    <button onClick={reset} aria-label="Close" data-testid="ai-close"><X size={18} /></button>
                </div>

                <div className="flex gap-px bg-[var(--border)]">
                    <button onClick={() => { setTab("paste"); setResult(null); setErr(""); }}
                        disabled={busy}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 text-xs font-mono tracking-[0.25em] uppercase ${tab==="paste"?"bg-[var(--surface-hover)] text-[var(--gold)]":"bg-[var(--surface)] text-[var(--text-muted)]"}`}
                        data-testid="ai-tab-paste">
                        <ClipboardPaste size={12} /> Paste text
                    </button>
                    <button onClick={() => { setTab("generate"); setResult(null); setErr(""); }}
                        disabled={busy}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 text-xs font-mono tracking-[0.25em] uppercase ${tab==="generate"?"bg-[var(--surface-hover)] text-[var(--gold)]":"bg-[var(--surface)] text-[var(--text-muted)]"}`}
                        data-testid="ai-tab-generate">
                        <Wand2 size={12} /> Generate from prompt
                    </button>
                </div>

                <div className="flex-1 overflow-auto p-5 space-y-4">
                    {!result && tab === "paste" && (
                        <>
                            <p className="text-xs text-[var(--text-muted)]">{endpoints.pasteHint}</p>
                            <textarea rows={10} className="input-dark font-mono text-xs" disabled={busy}
                                value={text} onChange={(e) => setText(e.target.value)}
                                placeholder={mode === "workout"
                                    ? "Upper push\nBench 4x8 @185\nOHP 4x6\nDips 3x12\nLateral raise 4x15"
                                    : "Day 1\nBreakfast: 3 egg whites + spinach\nDinner: grilled chicken + broccoli + sweet potato\nFruit: apple\n…"}
                                data-testid="ai-paste-text" />
                        </>
                    )}
                    {!result && tab === "generate" && (
                        <>
                            <p className="text-xs text-[var(--text-muted)]">{endpoints.genHint}</p>
                            <textarea rows={6} className="input-dark" disabled={busy}
                                value={prompt} onChange={(e) => setPrompt(e.target.value)}
                                placeholder={mode === "workout"
                                    ? "Example: leg day focused on glutes, 50 min, dumbbells + barbell"
                                    : "Example: 7-day fat-loss plan, 2 meals + fruit per day, spinach+eggs every breakfast, no dinner repeats, 3 ingredients per meal"}
                                data-testid="ai-generate-prompt" />
                            <p className="text-[10px] font-mono text-[var(--text-faint)]">
                                Big multi-day plans can take 60–120s. The job runs in the background — keep this window open and the result will pop in.
                            </p>
                        </>
                    )}
                    {busy && (
                        <div className="border border-[var(--gold-deep)] bg-[var(--surface-2)] p-4 flex items-center gap-3" data-testid="ai-progress">
                            <Loader2 size={16} className="animate-spin text-[var(--gold-bright)]" />
                            <div className="flex-1">
                                <p className="text-sm font-serif">{phaseLabel}</p>
                                <p className="text-[10px] font-mono text-[var(--text-muted)]">{elapsed}s elapsed · this is normal for big plans</p>
                            </div>
                        </div>
                    )}
                    {err && (
                        <div className="flex items-start gap-2 border border-[var(--danger)] bg-[var(--danger)]/10 p-3 text-sm text-[var(--danger)]" data-testid="ai-error">
                            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                            <div>{err}</div>
                        </div>
                    )}
                    {result && <AiResultPreview mode={mode} result={result} />}
                </div>

                <div className="border-t border-[var(--border)] p-4 flex items-center justify-end gap-2">
                    {!result ? (
                        <>
                            <button onClick={reset} disabled={busy} className="btn-ghost" data-testid="ai-cancel">{busy ? "Close" : "Cancel"}</button>
                            <button onClick={run}
                                disabled={busy || (tab === "paste" ? text.trim().length < 3 : prompt.trim().length < 3)}
                                className="btn-primary inline-flex items-center gap-2"
                                data-testid="ai-run">
                                {busy ? <><Loader2 size={14} className="animate-spin" /> {phaseLabel}</> : <><Sparkles size={14} /> {tab === "paste" ? "Parse" : "Generate"}</>}
                            </button>
                        </>
                    ) : (
                        <>
                            <button onClick={() => setResult(null)} className="btn-ghost" data-testid="ai-back">Back</button>
                            <button onClick={apply} className="btn-primary" data-testid="ai-apply">Apply to builder</button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function AiResultPreview({ mode, result }) {
    if (mode === "workout") {
        return (
            <div data-testid="ai-preview">
                <p className="label-tiny mb-2">/ Preview</p>
                <h4 className="font-serif text-2xl mb-1">{result.name}</h4>
                {result.description && <p className="text-sm text-[var(--text-muted)] mb-4">{result.description}</p>}
                {(result.days || []).map((d, i) => (
                    <div key={i} className="border border-[var(--border)] bg-[var(--surface-2)] p-3 mb-3">
                        <p className="font-display text-lg mb-2">Day {d.day} — {d.title}</p>
                        <ul className="text-xs font-mono space-y-0.5 text-[var(--text-muted)]">
                            {(d.exercises || []).map((ex, j) => (
                                <li key={j}>{ex.sets}×{ex.reps} <span className="text-[var(--text)]">{ex.name}</span> · rest {ex.rest}{ex.notes ? ` · ${ex.notes}` : ""}</li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        );
    }
    return (
        <div data-testid="ai-preview">
            <p className="label-tiny mb-2">/ Preview</p>
            <h4 className="font-serif text-2xl mb-1">{result.name}</h4>
            {result.description && <p className="text-sm text-[var(--text-muted)] mb-2">{result.description}</p>}
            <p className="text-xs font-mono mb-1 text-[var(--gold-bright)]">
                {result.daily_calories} kcal/day · P:{result.protein_g} C:{result.carbs_g} F:{result.fat_g}
                {result.days_count > 1 && <span className="text-[var(--text-muted)]"> · {result.days_count} days</span>}
            </p>
            {result.match_stats && (
                <p className="text-[10px] font-mono mb-3 text-[var(--text-muted)]">
                    FOOD LIBRARY MATCHED: {result.match_stats.matched}/{result.match_stats.total}
                    {result.match_stats.matched < result.match_stats.total && (
                        <span className="text-[var(--gold-deep)]"> · unmatched items use AI estimates</span>
                    )}
                </p>
            )}
            {(result.meals || []).map((m, i) => (
                <div key={i} className="border border-[var(--border)] bg-[var(--surface-2)] p-3 mb-3">
                    <p className="font-display text-lg mb-2">{m.name}</p>
                    <ul className="text-xs space-y-1 text-[var(--text-muted)]">
                        {(m.items || []).map((it, j) => {
                            if (typeof it === "string") return <li key={j}>• {it}</li>;
                            const totKcal = Math.round((it.kcal || 0) * (it.matched ? (it.servings || 1) : 1));
                            const totP = Math.round(((it.protein_g || 0) * (it.matched ? (it.servings || 1) : 1)) * 10) / 10;
                            const totC = Math.round(((it.carbs_g || 0) * (it.matched ? (it.servings || 1) : 1)) * 10) / 10;
                            const totF = Math.round(((it.fat_g || 0) * (it.matched ? (it.servings || 1) : 1)) * 10) / 10;
                            return (
                                <li key={j} className="flex items-start gap-2">
                                    {it.matched
                                        ? <span className="text-[var(--gold-bright)] shrink-0 font-mono">✓</span>
                                        : <span className="text-[var(--gold-deep)] shrink-0 font-mono" title="No library match — using AI estimate">~</span>}
                                    <span className="flex-1">
                                        <span className={it.matched ? "text-[var(--text)]" : ""}>{it.servings || 1}× {it.name}</span>
                                        <span className="ml-2 font-mono text-[10px]">{totKcal}kcal · P{totP} C{totC} F{totF}</span>
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ))}
        </div>
    );
}
